import { useMemo } from 'react';

import { ContentStrings, useGetContentStrings } from '~/data/contentStrings';
import { getCurrentLanguage } from '~/localization';
import { EmployeeModuleTabConfig } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

type PropsType<TabPath, TabConfigKey, TabInstructionKey> = {
  tabConfigs: EmployeeModuleTabConfig<
    TabPath,
    TabConfigKey,
    TabInstructionKey
  >[];
};

export const useModuleTabsInstructions = <
  TabPath,
  TabConfigKey,
  TabInstructionKey,
>(
  props: PropsType<TabPath, TabConfigKey, TabInstructionKey>,
) => {
  const { tabConfigs } = props;

  const currentLang = getCurrentLanguage();

  const instructionKeys: string[] = useMemo(() => {
    return tabConfigs.reduce((acc, config) => {
      if (!!config.instructionKey) {
        return [...acc, `${config.instructionKey}`];
      }

      return acc;
    }, [] as string[]);
  }, [tabConfigs]);

  const { isLoading, data: instructionStrings } = useGetContentStrings(
    {
      contentKeys: instructionKeys,
      langCode: currentLang,
    },
    { revalidateOnFocus: false, revalidateIfStale: false, refreshInterval: 0 },
  );

  const tabInstructions: ContentStrings = useMemo(() => {
    if (isLoading || typeof instructionStrings === 'undefined') {
      return {};
    }

    // Convert instructions to be keyed by tab path
    return tabConfigs.reduce((acc, config) => {
      const instructionKey = config.instructionKey as unknown as string;

      const instruction: string = Object.keys(instructionStrings).includes(
        instructionKey,
      )
        ? instructionStrings[instructionKey]
        : '';

      if (typeof instruction !== 'undefined' && instruction.length > 0) {
        return {
          ...acc,
          [config.path as unknown as string]: instruction,
        };
      }

      return acc;
    }, {} as ContentStrings);
  }, [isLoading, instructionStrings, tabConfigs]);

  return {
    isLoading,
    tabInstructions,
  };
};
